import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { hasCapability } from "@/lib/brokerAuth";
import {
  getNotificationPreferences,
  setEventEnabled,
  type NotificationChannelKey,
  type NotificationPreferencesView,
} from "@/lib/brokerNotificationPreferences";
import {
  getMarketingConsent,
  setMarketingConsent,
  type MarketingChannel,
  type MarketingConsentView,
} from "@/lib/brokerConsent";
import { messageFor } from "@/lib/errors";

// The broker's notification preference center (BRK-S158): notification delivery
// preferences (PMS-S351/S357) + marketing-communication consent (PMS-S346) — the
// two-axis model. Transactional notifications are MANDATORY (shown checked +
// locked; the server 400s an attempt to turn one off); only the advisory events
// and marketing consent are editable. Load-on-mount card stack (the SecurityPage
// idiom); every write re-renders from the server's returned view.

const CHANNEL_LABEL: Record<NotificationChannelKey, string> = {
  email: "Email",
  sms: "Text (SMS)",
  in_app: "In-app",
};

export function NotificationPreferencesPage() {
  const { identity, authedRequest } = useAuth();
  const canViewPrefs = hasCapability(identity, "notification-preference:view");
  const canEditPrefs = hasCapability(identity, "notification-preference:edit");
  const canViewConsent = hasCapability(identity, "consent:view");
  const canEditConsent = hasCapability(identity, "consent:edit");

  const [prefs, setPrefs] = useState<NotificationPreferencesView | null>(null);
  const [consent, setConsent] = useState<MarketingConsentView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setError(null);
    setLoading(true);
    (async () => {
      try {
        const [p, c] = await Promise.all([
          canViewPrefs ? getNotificationPreferences(authedRequest) : null,
          canViewConsent ? getMarketingConsent(authedRequest) : null,
        ]);
        if (!cancelled) {
          setPrefs(p);
          setConsent(c);
        }
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, canViewPrefs, canViewConsent]);

  useEffect(() => load(), [load]);

  const onToggleEvent = async (noticeType: string, enabled: boolean) => {
    setSavingKey(noticeType);
    setError(null);
    try {
      setPrefs(await setEventEnabled(authedRequest, { noticeType, enabled }));
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setSavingKey(null);
    }
  };

  const onToggleConsent = async (
    channel: MarketingChannel,
    granted: boolean,
  ) => {
    setSavingKey(`consent:${channel}`);
    setError(null);
    try {
      setConsent(
        await setMarketingConsent(authedRequest, { channel, granted }),
      );
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-6 py-8">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">
          Notification preferences
        </h1>
        <p className="text-brk-dim">
          Choose how we keep you posted. Transactional updates about your
          submissions and bonds are required and can&rsquo;t be turned off;
          marketing messages are always your choice.
        </p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
      {loading && (
        <p role="status" className="text-sm text-brk-dim">
          Loading your preferences…
        </p>
      )}

      {!loading && prefs && (
        <>
          <div className="flex flex-col gap-2 rounded-lg border border-brk-border bg-brk-panel p-5">
            <h2 className="font-semibold">Delivery channels</h2>
            <ul className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-brk-dim">
              {prefs.channels.map((c) => (
                <li key={c.channel}>
                  {CHANNEL_LABEL[c.channel]}:{" "}
                  <span
                    className={c.available ? "text-brk-text" : "text-brk-dim"}
                  >
                    {c.available ? "on" : "coming soon"}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {prefs.categories.map((cat) => (
            <div
              key={cat.category}
              className="flex flex-col gap-3 rounded-lg border border-brk-border bg-brk-panel p-5"
            >
              <h2 className="font-semibold">{cat.label}</h2>
              <ul className="flex flex-col divide-y divide-brk-border">
                {cat.events.map((ev) => (
                  <li
                    key={ev.noticeType}
                    className="flex items-center justify-between gap-4 py-2 first:pt-0 last:pb-0"
                  >
                    <span className="min-w-0 text-sm text-brk-text">
                      {ev.label}
                      {ev.locked && (
                        <span className="ml-2 text-xs text-brk-dim">
                          Required
                        </span>
                      )}
                    </span>
                    <input
                      type="checkbox"
                      aria-label={ev.label}
                      className="h-4 w-4 shrink-0 accent-brk-accent disabled:opacity-60"
                      checked={ev.enabled}
                      disabled={
                        ev.locked ||
                        !canEditPrefs ||
                        savingKey === ev.noticeType
                      }
                      onChange={(e) =>
                        onToggleEvent(ev.noticeType, e.target.checked)
                      }
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}

      {!loading && consent && (
        <div className="flex flex-col gap-3 rounded-lg border border-brk-border bg-brk-panel p-5">
          <div className="flex flex-col gap-1">
            <h2 className="font-semibold">Marketing communications</h2>
            <p className="text-sm text-brk-dim">
              Optional updates about new products and offers. Turning these off
              never affects the transactional notices above.
            </p>
          </div>
          <ul className="flex flex-col divide-y divide-brk-border">
            {consent.channels.map((c) => (
              <li
                key={c.channel}
                className="flex items-center justify-between gap-4 py-2 first:pt-0 last:pb-0"
              >
                <span className="text-sm text-brk-text">
                  {CHANNEL_LABEL[c.channel]}
                </span>
                <input
                  type="checkbox"
                  aria-label={`Marketing ${CHANNEL_LABEL[c.channel]}`}
                  className="h-4 w-4 shrink-0 accent-brk-accent disabled:opacity-60"
                  checked={c.granted}
                  disabled={
                    !canEditConsent || savingKey === `consent:${c.channel}`
                  }
                  onChange={(e) => onToggleConsent(c.channel, e.target.checked)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      <Link to="/portal" className="text-sm text-brk-accent hover:underline">
        ← Back to portal
      </Link>
    </section>
  );
}
